import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../config';
import { toast } from 'sonner';
import CoperturaStatsDialog from './CoperturaStatsDialog';
import SsmateStatsDialog from './SsmateStatsDialog';
import VfOpzioniDialog from './VfOpzioniDialog';
import VfStatsDialog from './VfStatsDialog';
import IndagaStatsDialog from './IndagaStatsDialog';
import CampagneStatsDialog, { IconCampagne } from './CampagneStatsDialog';
import {
  COLORE_CAMPAGNE, COLORE_ERRORI, COLORE_STANDBY, statoAgente, titoloSpia, statoGestione,
} from './campagneAgenteData';
import DisservizioDialog from './DisservizioDialog';
import StatsDialog from './StatsDialog';
import { APRI_STATS } from './BulkProgress';
import AgentPriorityDialog from './AgentPriorityDialog';
import PianificazioneSsmateDialog from './PianificazioneSsmateDialog';
import AgentiManagerDialog from './AgentiManagerDialog';
import VodafoneBadge from './VodafoneBadge';
import Avatar from './Avatar';
import CambiaPasswordCard from './CambiaPasswordCard';
import QrModal from './QrModal';
import Logo from '../mobile/Logo';
import './sidebarnav.css';

const IconKey = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="7.5" cy="15.5" r="3.5" /><path d="M10 13l9-9M16 4l3 3M14 6l2 2" /></svg>
);
const IconLogout = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3M16 17l5-5-5-5M21 12H9" /></svg>
);
const IconQr = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><path d="M14 14h3v3h-3zM21 14v7M17 21h4" /></svg>
);
const IconChart = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><rect x="7" y="11" width="3" height="6" /><rect x="12" y="7" width="3" height="10" /><rect x="17" y="13" width="3" height="4" /></svg>
);
const IconUsers = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
);
const IconSliders = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="5" y1="21" x2="5" y2="14" /><line x1="5" y1="10" x2="5" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
    <line x1="19" y1="21" x2="19" y2="16" /><line x1="19" y1="12" x2="19" y2="3" />
    <circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="10" r="1.7" /><circle cx="19" cy="14" r="1.7" />
  </svg>
);
import { useAuth } from '../auth';
import { useCopertura } from '../coperturaState';
import {
  IconDashboard, IconImport, IconList, IconExport, IconSearch, IconSettings, IconPin,
  IconSignal, IconTools, IconActivity, IconGlobe, IconPhone, IconBuilding,
} from './Icons';

// Spia agente (stile cruscotto): spenta = grigia, attiva = colore + pulsa.
function Spia({ icon: Icon, label, on, color, onClick, attesa, operativo, allarme }) {
  return (
    <button className={`agent-spia${on ? ' on' : ''}${attesa ? ' agente-attesa' : ''}${operativo ? ' operativo' : ''}${allarme ? ' allarme' : ''}`} style={on ? { '--spia': color } : undefined}
      onClick={onClick} title={label} aria-label={label}>
      <Icon size={16} />
      {allarme && <span className="agent-spia-allarme" aria-hidden="true">!</span>}
    </button>
  );
}

// Interruttore mini (sopra la spia, in modalità controlli): on/off dell'agente.
function MiniToggle({ on, color, disabled, onClick, title }) {
  return (
    <button type="button" className={`agent-mini${on ? ' on' : ''}`}
      style={on ? { '--spia': color } : undefined} onClick={onClick} disabled={disabled} title={title}>
      <span className="agent-mini-knob" />
    </button>
  );
}

// Pulsante priorità (barre crescenti) accanto al toggle, in modalità controlli.
function PrioBtn({ color, onClick, title }) {
  return (
    <button type="button" className="agent-mini-prio" style={{ '--spia': color }} onClick={onClick} title={title}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
        <line x1="5" y1="19" x2="5" y2="14" /><line x1="12" y1="19" x2="12" y2="9" /><line x1="19" y1="19" x2="19" y2="4" />
      </svg>
    </button>
  );
}

// Voce del flyout (gated a monte con can(...))
function SubLink({ to, end, icon, children, style }) {
  return (
    <NavLink to={to} end={end} style={style} className={({ isActive }) => `snav-i${isActive ? ' active' : ''}`}>
      <span className="nav-icon">{icon}</span><span style={{ flex: 1 }}>{children}</span>
    </NavLink>
  );
}
// Gruppo con testata + flyout a destra (cascata). Nasconde il gruppo se non ha voci permesse.
function NavGroup({ label, icon, paths = [], children }) {
  const location = useLocation();
  const kids = React.Children.toArray(children).filter(Boolean);
  if (!kids.length) return null;
  const active = paths.includes(location.pathname);
  return (
    <div className="snav-g">
      <div className={`snav-h${active ? ' active' : ''}`}>
        <span className="nav-icon">{icon}</span><span style={{ flex: 1 }}>{label}</span><span className="cv">▸</span>
      </div>
      <div className="snav-fly">
        <div className="snav-fh">{label}</div>
        {kids.map((k, i) => React.cloneElement(k, { key: i, style: { ...(k.props.style || {}), transitionDelay: `${i * 32}ms` } }))}
      </div>
    </div>
  );
}

export default function Sidebar() {
  const location = useLocation();
  const { user, logout, can } = useAuth();
  const { abilitata: coperturaAbilitata } = useCopertura();
  const [a456attivo, setA456attivo] = useState(false);
  const [a456inAttesa, setA456inAttesa] = useState(false);
  const [copAttivo, setCopAttivo] = useState(false);
  const [copInAttesa, setCopInAttesa] = useState(false);
  const [copStatsOpen, setCopStatsOpen] = useState(false);
  const [ssmateAttivo, setSsmateAttivo] = useState(false);
  const [ssmateInAttesa, setSsmateInAttesa] = useState(false);
  const [geaAttivo, setGeaAttivo] = useState(false);
  const [geaInAttesa, setGeaInAttesa] = useState(false);
  // Vodafone ci sta bloccando: la spia lo dice col punto esclamativo, anche ad agente spento
  const [geaBloccato, setGeaBloccato] = useState(false);
  const [ssStatsOpen, setSsStatsOpen] = useState(false);
  const [geaStatsOpen, setGeaStatsOpen] = useState(false);
  const [indagaAttivo, setIndagaAttivo] = useState(false);
  const [indagaInAttesa, setIndagaInAttesa] = useState(false);
  const [indagaStatsOpen, setIndagaStatsOpen] = useState(false);
  // agente Campagne: lo stato intero di /campagne/agente (attivo, in attesa, captcha, errori)
  const [campAgente, setCampAgente] = useState(null);
  const [campStatsOpen, setCampStatsOpen] = useState(false);
  const [agentsCfg, setAgentsCfg] = useState(false);   // modalità controlli (toggle sopra le spie)
  const [prioAgent, setPrioAgent] = useState(null);    // dialog priorità per agente ('456'|'copertura'|'ssmate')
  const [busy, setBusy] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);

  // Il progresso dei bulk (in basso a destra) chiede l'apertura della finestra statistiche
  // dello strumento: le finestre sono gia' montate qui, quindi basta accendere la sua.
  useEffect(() => {
    const apri = (e) => ({
      456: () => setStatsDialogOpen(true), copertura: () => setCopStatsOpen(true),
      ssmate: () => setSsStatsOpen(true), gea: () => setGeaStatsOpen(true),
      indaga: () => setIndagaStatsOpen(true),
    }[e.detail]?.());
    window.addEventListener(APRI_STATS, apri);
    return () => window.removeEventListener(APRI_STATS, apri);
  }, []);
  const [agentStandby, setAgentStandby] = useState(false);
  const [standbyUntil, setStandbyUntil] = useState(null);
  const [obiettiviRaggiunti, setObiettiviRaggiunti] = useState(0);
  const [disservizioOpen, setDisservizioOpen] = useState(false);
  const [pianifOpen, setPianifOpen] = useState(false);
  const [dockOpen, setDockOpen] = useState(false);   // sportellino spie aperto/chiuso
  const [agentiMgrOpen, setAgentiMgrOpen] = useState(false);   // dialog gestione agenti
  const [vfOpzioniOpen, setVfOpzioniOpen] = useState(false);   // parametri dell'agente VF
  // Sidebar sempre espansa: rimuovo eventuale stato "compresso" persistito da prima.
  useEffect(() => {
    document.body.classList.remove('sidebar-collapsed');
    try { localStorage.removeItem('sidebarCollapsed'); } catch {}
  }, []);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/agent/status`);
      setA456attivo(!!r.data.attivo);
      setA456inAttesa(!!r.data.in_attesa);
      const job = r.data.job;
      const standby = !!job && job.status === 'standby';
      setAgentStandby(standby);
      setStandbyUntil(standby ? (job.standby_until || null) : null);
      setObiettiviRaggiunti(r.data.obiettivi_raggiunti || 0);
    } catch {}
    if (can('agente_copertura')) {
      try {
        const cs = await axios.get(`${API}/copertura/stato`);
        setCopAttivo(!!cs.data?.agente_attivo);
        setCopInAttesa(!!cs.data?.in_attesa);
      } catch {}
    }
    if (can('ssmate')) {
      try {
        const ss = await axios.get(`${API}/ssmate/bulk/preview`);
        setSsmateAttivo(!!ss.data?.agente_attivo);
        setSsmateInAttesa(!!ss.data?.in_attesa);
      } catch {}
    }
    if (can('gea')) {
      try {
        const g = await axios.get(`${API}/gea/stato`);
        setGeaAttivo(!!g.data?.agente_attivo);
        setGeaInAttesa(!!g.data?.in_attesa);
        setGeaBloccato(!!g.data?.blocco?.bloccato);
      } catch {}
    }
    if (can('indaga')) {
      try {
        const iq = await axios.get(`${API}/indaga/stato`);
        setIndagaAttivo(!!iq.data?.agente_attivo);
        setIndagaInAttesa(!!iq.data?.in_attesa);
      } catch {}
    }
    if (can('campagne')) {
      try {
        const ca = await axios.get(`${API}/campagne/agente`);
        setCampAgente(ca.data || null);
      } catch {}
    }
  };
  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 8000);
    return () => clearInterval(interval);
  }, []);  // eslint-disable-line

  const toggleAgente = async (url, next, nome) => {
    setBusy(true);
    try {
      await axios.post(url, { attivo: next });
      toast.success(`AI ${nome} ${next ? 'attivato' : 'fermato'}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setBusy(false);
  };

  // Un agente è "operativo" solo se sta lavorando ADESSO: acceso, non in attesa, non in standby/errore.
  const op456 = a456attivo && !a456inAttesa && !agentStandby;
  const opCop = copAttivo && !copInAttesa;
  const opSsmate = ssmateAttivo && !ssmateInAttesa;
  const opGea = geaAttivo && !geaInAttesa;
  const campStato = statoAgente(campAgente);
  const opCampagne = campStato.chiave === 'attivo';
  const qualcunoOperativo = op456 || opCop || opSsmate || opGea || opCampagne;   // -> LED pulsa solo qui
  const campOn = campStato.chiave !== 'spento';
  // colore della spia: il suo azzurro; ambra se aspetta il captcha, rosso se fermo per errori
  const campColore = campStato.chiave === 'errori' ? COLORE_ERRORI
    : campStato.chiave === 'standby' ? COLORE_STANDBY : COLORE_CAMPAGNE;

  // Lista agenti per il dialog di gestione (solo quelli permessi all'utente).
  const st = (on, attesa, standby) => (standby ? 'disservizio' : (on ? (attesa ? 'in attesa' : 'attivo') : 'spento'));
  const agentiItems = [
    { key: '456', label: 'AI 456', color: '#22c55e', icon: <IconPhone size={18} />,
      on: a456attivo, statusLabel: st(a456attivo, a456inAttesa, agentStandby),
      toggleUrl: `${API}/agent/toggle`, nome: '456', hasPriority: true },
    ...(can('agente_copertura') && coperturaAbilitata ? [{ key: 'copertura', label: 'AI Copertura', color: '#3b82f6', icon: <IconSignal size={18} />,
      on: copAttivo, statusLabel: st(copAttivo, copInAttesa), toggleUrl: `${API}/copertura/agente`, nome: 'copertura', hasPriority: true }] : []),
    ...(can('ssmate') ? [{ key: 'ssmate', label: 'AI SSMATE', color: '#eab308', icon: <IconBuilding size={18} />,
      on: ssmateAttivo, statusLabel: st(ssmateAttivo, ssmateInAttesa), toggleUrl: `${API}/ssmate/agente`, nome: 'SSMATE', hasPriority: true, hasScheduling: true }] : []),
    ...(can('gea') ? [{ key: 'gea', label: 'AI VF', color: '#e60000', icon: <VodafoneBadge size={18} />,
      on: geaAttivo, statusLabel: st(geaAttivo, geaInAttesa), toggleUrl: `${API}/gea/agente`, nome: 'VF',
      hasPriority: true, hasOpzioni: true }] : []),
    // le Campagne non hanno priorità: lavorano tutte le righe accese, nell'ordine in cui ci sono
    ...(can('campagne') ? [{ key: 'campagne', label: 'AI Da qualificare', color: COLORE_CAMPAGNE, icon: <IconCampagne size={18} />,
      on: campOn, statusLabel: statoGestione(campAgente), toggleUrl: `${API}/campagne/agente`, nome: 'Campagne' }] : []),
  ];

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <Logo size={42} />
        <div className="sidebar-brand-txt">
          <div className="sb-name">Centro Servizi</div>
        </div>
      </div>
      <nav className="sidebar-nav snav">
        <NavGroup label="Generale" icon={<IconDashboard />}
          paths={['/', '/export-hub', '/esportabili', '/analisi', '/utilizzo', '/numeri', '/import', '/export', '/copertura']}>
          {can('dashboard') && <SubLink to="/" end icon={<IconDashboard size={16} />}>Dashboard</SubLink>}
          {can('export_hub') && <SubLink to="/export-hub" icon={<IconExport size={16} />}>Export Hub</SubLink>}
          {can('priorita') && <SubLink to="/esportabili" icon={<IconExport size={16} />}>Priorità</SubLink>}
          {can('analisi') && <SubLink to="/analisi" icon={<IconChart size={16} />}>Analisi</SubLink>}
          {user?.ruolo === 'admin' && <SubLink to="/utilizzo" icon={<IconUsers size={16} />}>Utilizzo</SubLink>}
          {can('numeri') && <SubLink to="/numeri" icon={<IconList size={16} />}>Elenco numeri</SubLink>}
          {can('import') && <SubLink to="/import" icon={<IconImport size={16} />}>Import</SubLink>}
          {can('export') && <SubLink to="/export" icon={<IconExport size={16} />}>Export</SubLink>}
          {can('copertura') && <SubLink to="/copertura" icon={<IconPin size={16} />}>Copertura liste</SubLink>}
        </NavGroup>

        <NavGroup label="Agenti" icon={<IconPhone />}
          paths={['/agente-456', '/verifiche', '/cruscotto-456', '/coperture', '/ssmate', '/agente-vf']}>
          {can('verifiche') && <SubLink to="/agente-456" icon={<IconDashboard size={16} />}>AI 456 · Cruscotto</SubLink>}
          {can('verifiche') && <SubLink to="/verifiche" icon={<IconSearch size={16} />}>Verifiche</SubLink>}
          {can('cruscotto_456') && <SubLink to="/cruscotto-456" icon={<IconPhone size={16} />}>Cruscotto 456</SubLink>}
          {/* «AI Copertura» e «Copertura SSMATE» erano due voci per la stessa domanda:
              ora una sola pagina a tab (SalesMate / sito TIM / proiezione). */}
          {(can('agente_copertura') || can('ssmate_copertura')) && <SubLink to="/coperture" icon={<IconSignal size={16} />}>Copertura</SubLink>}
          {can('ssmate') && <SubLink to="/ssmate" icon={<IconBuilding size={16} />}>AI SSMATE</SubLink>}
          {can('gea') && <SubLink to="/agente-vf" icon={<VodafoneBadge size={16} />}>AI VF</SubLink>}
        </NavGroup>

        <NavGroup label="Lavoro" icon={<IconTools />}
          paths={['/ricerca-online', '/strumenti', '/domini-rivedere', '/campagne', '/indaga', '/aziende-da-completare', '/salute']}>
          {can('ricerca_online') && <SubLink to="/ricerca-online" icon={<IconGlobe size={16} />}>Verifica online</SubLink>}
          {can('strumenti') && <SubLink to="/strumenti" icon={<IconTools size={16} />}>Strumenti</SubLink>}
          {can('domini_rivedere') && <SubLink to="/domini-rivedere" icon={<IconGlobe size={16} />}>Domini da rivedere</SubLink>}
          {can('campagne') && <SubLink to="/campagne" icon={<IconImport size={16} />}>Da qualificare</SubLink>}
          {can('indaga') && <SubLink to="/indaga" icon={<IconGlobe size={16} />}>Agente Indaga</SubLink>}
          {can('vicine_completare') && <SubLink to="/aziende-da-completare" icon={<IconBuilding size={16} />}>Aziende da completare</SubLink>}
          {can('salute') && <SubLink to="/salute" icon={<IconActivity size={16} />}>Salute</SubLink>}
        </NavGroup>
      </nav>
      <StatsDialog open={statsDialogOpen} onClose={() => setStatsDialogOpen(false)} />
      <CoperturaStatsDialog open={copStatsOpen} onClose={() => setCopStatsOpen(false)} />
      <SsmateStatsDialog open={ssStatsOpen} onClose={() => setSsStatsOpen(false)} />
      <VfStatsDialog open={geaStatsOpen} onClose={() => setGeaStatsOpen(false)} />
      <IndagaStatsDialog open={indagaStatsOpen} onClose={() => setIndagaStatsOpen(false)} />
      <CampagneStatsDialog open={campStatsOpen} onClose={() => { setCampStatsOpen(false); refresh(); }} />
      <AgentPriorityDialog agente={prioAgent} open={!!prioAgent} onClose={() => setPrioAgent(null)} />
      <DisservizioDialog open={disservizioOpen} onClose={() => setDisservizioOpen(false)} standbyUntil={standbyUntil} />
      <PianificazioneSsmateDialog open={pianifOpen} onClose={() => setPianifOpen(false)} />
      <AgentiManagerDialog open={agentiMgrOpen} onClose={() => setAgentiMgrOpen(false)}
        items={agentiItems} busy={busy} onToggle={toggleAgente}
        onPriority={(k) => setPrioAgent(k)} onScheduling={() => setPianifOpen(true)}
        onOpzioni={() => setVfOpzioniOpen(true)} />
      <VfOpzioniDialog open={vfOpzioniOpen} onClose={() => setVfOpzioniOpen(false)} />
      <div className={`agent-dock${dockOpen ? ' open' : ''}`}>
        <button type="button" className={`agent-edge${qualcunoOperativo ? ' live' : ''}`}
          onClick={() => setDockOpen((o) => !o)}
          title={dockOpen ? 'Chiudi spie agenti' : 'Spie agenti'} aria-label="Spie agenti" />
        <div className="agent-panel">
          <button className="agent-dock-cfg" onClick={() => setAgentiMgrOpen(true)}
            title="Gestione agenti (attiva/disattiva e priorità)">
            <IconSliders size={14} />
          </button>
          <div className="agent-spie">
          <div className="agent-col">
            {agentsCfg && (
              <MiniToggle on={a456attivo} color="#22c55e" disabled={busy}
                title={a456attivo ? 'Spegni agente 456' : 'Accendi agente 456'}
                onClick={() => toggleAgente(`${API}/agent/toggle`, !a456attivo, '456')} />
            )}
            {agentsCfg && <PrioBtn color="#22c55e" title="Priorità agente 456" onClick={() => setPrioAgent('456')} />}
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <Spia icon={IconPhone}
                label={agentStandby ? 'AI 456 — disservizio' : (a456attivo ? (a456inAttesa ? 'AI 456 — in attesa' : 'AI 456 — attivo') : 'AI 456 — spento')}
                on={a456attivo || agentStandby} color={agentStandby ? '#f59e0b' : '#22c55e'}
                attesa={a456attivo && a456inAttesa && !agentStandby}
                operativo={a456attivo && !a456inAttesa && !agentStandby}
                onClick={() => (agentStandby ? setDisservizioOpen(true) : setStatsDialogOpen(true))} />
              {obiettiviRaggiunti > 0 && (
                <span title={`${obiettiviRaggiunti} obiettivi raggiunti — clicca per leggere`}
                  style={{ position: 'absolute', top: -3, right: -3, minWidth: 15, height: 15, padding: '0 3px',
                    borderRadius: 8, background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  {obiettiviRaggiunti}
                </span>
              )}
            </span>
          </div>
          {can('agente_copertura') && coperturaAbilitata && (
            <div className="agent-col">
              {agentsCfg && (
                <MiniToggle on={copAttivo} color="#3b82f6" disabled={busy}
                  title={copAttivo ? 'Spegni agente copertura' : 'Accendi agente copertura'}
                  onClick={() => toggleAgente(`${API}/copertura/agente`, !copAttivo, 'copertura')} />
              )}
              {agentsCfg && <PrioBtn color="#3b82f6" title="Priorità agente copertura" onClick={() => setPrioAgent('copertura')} />}
              <Spia icon={IconSignal}
                label={copAttivo ? (copInAttesa ? 'AI Copertura — in attesa' : 'AI Copertura — attivo') : 'AI Copertura — spento'}
                on={copAttivo} color="#3b82f6"
                attesa={copAttivo && copInAttesa}
                operativo={copAttivo && !copInAttesa}
                onClick={() => setCopStatsOpen(true)} />
            </div>
          )}
          {can('ssmate') && (
            <div className="agent-col">
              {agentsCfg && (
                <MiniToggle on={ssmateAttivo} color="#eab308" disabled={busy}
                  title={ssmateAttivo ? 'Spegni agente SSMATE' : 'Accendi agente SSMATE'}
                  onClick={() => toggleAgente(`${API}/ssmate/agente`, !ssmateAttivo, 'SSMATE')} />
              )}
              {agentsCfg && <PrioBtn color="#eab308" title="Priorità agente SSMATE" onClick={() => setPrioAgent('ssmate')} />}
              {agentsCfg && (
                <button type="button" className="agent-prio-btn" title="Fascia oraria agente SSMATE"
                  onClick={() => setPianifOpen(true)}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
                  </svg>
                </button>
              )}
              <Spia icon={IconBuilding}
                label={ssmateAttivo ? (ssmateInAttesa ? 'AI SSMATE — in attesa' : 'AI SSMATE — attivo') : 'AI SSMATE — spento'}
                on={ssmateAttivo} color="#eab308"
                attesa={ssmateAttivo && ssmateInAttesa}
                operativo={ssmateAttivo && !ssmateInAttesa}
                onClick={() => setSsStatsOpen(true)} />
            </div>
          )}
          {can('gea') && (
            <div className="agent-col">
              {agentsCfg && (
                <MiniToggle on={geaAttivo} color="#e60000" disabled={busy}
                  title={geaAttivo ? 'Spegni AI VF' : 'Accendi AI VF'}
                  onClick={() => toggleAgente(`${API}/gea/agente`, !geaAttivo, 'VF')} />
              )}
              {agentsCfg && <PrioBtn color="#e60000" title="Priorità ed esclusioni AI VF" onClick={() => setPrioAgent('gea')} />}
              <button type="button" className={`agent-spia${geaAttivo ? ' on' : ''}${geaAttivo && geaInAttesa ? ' agente-attesa' : ''}${geaAttivo && !geaInAttesa ? ' operativo' : ''}${geaBloccato ? ' allarme' : ''}`} style={geaAttivo ? { '--spia': '#f91600' } : undefined}
                title={geaBloccato ? 'AI VF — Vodafone ci sta bloccando' : (geaAttivo ? (geaInAttesa ? 'AI VF — in attesa' : 'AI VF — attivo') : 'AI VF — spento')}
                onClick={() => setGeaStatsOpen(true)}>
                <VodafoneBadge size={16} style={geaAttivo ? undefined : { filter: 'grayscale(1)', opacity: 0.55 }} />
                {geaBloccato && <span className="agent-spia-allarme" aria-hidden="true">!</span>}
              </button>
            </div>
          )}
          {can('indaga') && (
            <div className="agent-col">
              {agentsCfg && (
                <MiniToggle on={indagaAttivo} color="#f97316" disabled={busy}
                  title={indagaAttivo ? 'Spegni AI Indaga' : 'Accendi AI Indaga'}
                  onClick={() => toggleAgente(`${API}/indaga/agente`, !indagaAttivo, 'Indaga')} />
              )}
              <button type="button" className={`agent-spia${indagaAttivo ? ' on' : ''}${indagaAttivo && indagaInAttesa ? ' agente-attesa' : ''}${indagaAttivo && !indagaInAttesa ? ' operativo' : ''}`}
                style={indagaAttivo ? { '--spia': '#f97316' } : { color: '#f97316', borderColor: 'rgba(249,115,22,.5)' }}
                title={indagaAttivo ? (indagaInAttesa ? 'AI Indaga — in attesa' : 'AI Indaga — attivo') : 'AI Indaga — spento'}
                onClick={() => setIndagaStatsOpen(true)}>
                <svg viewBox="0 0 24 24" width="23" height="23" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={indagaAttivo ? undefined : { opacity: .6 }}>
                  <circle cx="10" cy="10" r="7.6" /><path d="M21.5 21.5 15.4 15.4" />
                  <path d="M10 5.9c-1.85 0-3.25 1.35-3.25 3.1 0 2.1 3.25 5.1 3.25 5.1s3.25-3 3.25-5.1C13.25 7.25 11.85 5.9 10 5.9z" />
                  <circle cx="10" cy="9.1" r="1.15" fill="currentColor" stroke="none" />
                </svg>
              </button>
            </div>
          )}
          {can('campagne') && (
            <div className="agent-col">
              {agentsCfg && (
                <MiniToggle on={campOn} color={COLORE_CAMPAGNE} disabled={busy}
                  title={campOn ? 'Spegni AI Campagne' : 'Accendi AI Campagne'}
                  onClick={() => toggleAgente(`${API}/campagne/agente`, !campOn, 'Campagne')} />
              )}
              {/* nessun pulsante priorità: le Campagne non ne hanno */}
              <button type="button"
                className={`agent-spia${campOn ? ' on' : ''}${campStato.chiave === 'attesa' || campStato.chiave === 'standby' ? ' agente-attesa' : ''}${opCampagne ? ' operativo' : ''}`}
                style={campOn ? { '--spia': campColore } : { color: COLORE_CAMPAGNE, borderColor: 'rgba(14,165,233,.5)' }}
                title={titoloSpia(campAgente)} aria-label={titoloSpia(campAgente)}
                onClick={() => setCampStatsOpen(true)}>
                <IconCampagne size={22} style={campOn ? undefined : { opacity: .6 }} />
              </button>
            </div>
          )}
          </div>
        </div>
      </div>
      {dockOpen && <div className="agent-dock-overlay" onClick={() => setDockOpen(false)} />}
      <div className="sidebar-avatar-wrap">
        <button type="button" className="sidebar-avatar" title={user?.username} onClick={() => setAvatarOpen(o => !o)}>
          <Avatar username={user?.username} size={34} color="var(--accent-light)" />
        </button>
        {avatarOpen && <div className="avatar-overlay" onClick={() => setAvatarOpen(false)} />}
        <div className={`avatar-menu${avatarOpen ? ' open' : ''}`}>
          <div className="avatar-menu-head">
            <Avatar username={user?.username} size={40} color="var(--accent-dark)" />
            <div style={{ minWidth: 0 }}>
              <div className="avatar-menu-name">{user?.username}</div>
              <span className={`avatar-role-badge ${user?.ruolo === 'admin' ? 'admin' : 'op'}`}>
                {user?.ruolo === 'admin' ? 'Amministratore' : 'Operatore'}
              </span>
            </div>
          </div>
          <button className="avatar-menu-item" onClick={() => { setAvatarOpen(false); setQrOpen(true); }}><IconQr /> QR code app mobile</button>
          <button className="avatar-menu-item" onClick={() => { setAvatarOpen(false); setShowPwd(true); }}><IconKey /> Cambia password</button>
          <button className="avatar-menu-item danger" onClick={() => { setAvatarOpen(false); logout(); }}><IconLogout /> Esci</button>
        </div>
        {can('impostazioni') && (
          <NavLink to="/impostazioni" className={({ isActive }) => `sidebar-gear${isActive ? ' active' : ''}`} title="Impostazioni">
            <IconSettings size={18} />
          </NavLink>
        )}
      </div>
      {showPwd && (
        <div className="pwd-modal-overlay" onClick={() => setShowPwd(false)}>
          <div className="pwd-modal" onClick={(e) => e.stopPropagation()}>
            <CambiaPasswordCard />
            <button className="btn btn-secondary" onClick={() => setShowPwd(false)}>Chiudi</button>
          </div>
        </div>
      )}
      <QrModal open={qrOpen} onClose={() => setQrOpen(false)} />
      <div className="sidebar-branding">
        <img src="/logo-sonitech.jpg" alt="Sonitech" className="sidebar-brand-logo" />
      </div>
    </aside>
  );
}
